import { NavigateFunction } from 'react-router'

import { NotificationType } from '@/schema'
import {
  buildDeliverableSetDocument,
  buildDeliverableSetRoute,
} from '@/utils/routes'

import { NotificationMetadata } from '../types'

export interface NavigationHandlerDependencies {
  navigate:
    | NavigateFunction
    | ((path: string, options?: { state?: Record<string, unknown> }) => void)
  setOpenDocument: (documents: Array<{ id: string; filename: string }>) => void
  setActiveDocumentId: (id: string) => void
  closeDrawer: () => void
  markAsRead: () => void | Promise<void>
  isMentioned?: boolean
}

const isDeliverableNavigation = (
  notificationType: NotificationType
): boolean => {
  return (
    notificationType === NotificationType.AssignedDeliverable ||
    notificationType === NotificationType.ReassignedDeliverable ||
    notificationType === NotificationType.Deadline ||
    notificationType === NotificationType.DeadlineApproaching ||
    notificationType === NotificationType.DeadlinePassed
  )
}

const navigateToDeliverable = (
  deliverableSetId: string,
  navigate:
    | NavigateFunction
    | ((path: string, options?: { state?: Record<string, unknown> }) => void)
): void => {
  navigate(
    buildDeliverableSetRoute(deliverableSetId),
    // State to store notification deliverable set ID for notification navigation
    { state: { deliverableSetId } }
  )
}

const navigateToDocument = (
  metadata: NotificationMetadata,
  dependencies: Pick<
    NavigationHandlerDependencies,
    'navigate' | 'setOpenDocument' | 'setActiveDocumentId' | 'isMentioned'
  >
): void => {
  const {
    document_id,
    deliverable_set_id,
    document_version_id,
    comment_id,
    root_comment_id,
    thread_id,
  } = metadata

  const filename = metadata.document_name || ''
  if (!document_id || !deliverable_set_id) {
    return
  }

  const { navigate, setOpenDocument, setActiveDocumentId, isMentioned } =
    dependencies

  const documentData = {
    id: document_id,
    filename: filename,
  }

  setOpenDocument([documentData])
  setActiveDocumentId(document_id)

  const route = buildDeliverableSetDocument(deliverable_set_id)

  // Use thread_id to find the parent thread in the thread list.
  // When thread_id is available and comment_id is different, the comment is a
  // reply and we pass comment_id as replyId so CommentDetail can scroll to it.
  const parentId = root_comment_id || thread_id
  const isReply = !!parentId && parentId !== comment_id

  navigate(route, {
    state: {
      document: documentData,
      activeDocumentId: document_id,
      versionId: document_version_id,
      commentId: parentId || comment_id,
      replyId: isReply ? comment_id : undefined,
      isNotification: true,
      isMentioned,
    },
  })
}

export const createNotificationNavigationHandler = (
  metadata: NotificationMetadata,
  notificationType: NotificationType,
  dependencies: NavigationHandlerDependencies
): (() => void) => {
  const {
    navigate,
    setOpenDocument,
    setActiveDocumentId,
    closeDrawer,
    markAsRead,
  } = dependencies

  return () => {
    const { deliverable_set_id } = metadata

    if (deliverable_set_id && isDeliverableNavigation(notificationType)) {
      navigateToDeliverable(deliverable_set_id, navigate)
    } else {
      navigateToDocument(metadata, {
        navigate,
        setOpenDocument,
        setActiveDocumentId,
        isMentioned: notificationType === NotificationType.UserMentioned,
      })
    }

    markAsRead()

    closeDrawer()
  }
}
